var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { isDefined, safeEval } from '@showroomjs/core';
import { Button, Checkbox, ColorInput, FileInput, NumberInput, Popover, Select, styled, Textarea, TextInput, } from '@showroomjs/ui';
import * as React from 'react';
import { HexColorPicker } from 'react-colorful';
import stringifyObject from 'stringify-object';
export const PropsEditor = (_a) => {
    var { editor } = _a, rootProps = __rest(_a, ["editor"]);
    return (React.createElement(Root, Object.assign({}, rootProps), editor.controls.map((ctrl) => {
        if (ctrl.type === 'checkbox') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key, css: {
                        marginBottom: '$4',
                        '@sm': {
                            marginBottom: 0,
                        },
                    } }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        marginBottom: '$4',
                        '@sm': {
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(Checkbox, { checked: isDefined(ctrl.value) ? ctrl.value : 'indeterminate', onCheckedChange: ctrl.setValue, id: ctrl.key }))));
        }
        if (ctrl.type === 'select') {
            return React.createElement(PropsSelectControl, { control: ctrl, key: ctrl.key });
        }
        if (ctrl.type === 'text') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        gridColumn: '1 / -1',
                        marginBottom: '$4',
                        '@sm': {
                            gridColumn: 'auto',
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(TextInput, { id: ctrl.key, value: ctrl.value || '', onValue: ctrl.setValue, css: {
                            maxWidth: '36rem',
                        } }))));
        }
        if (ctrl.type === 'number') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        gridColumn: '1 / -1',
                        marginBottom: '$4',
                        '@sm': {
                            gridColumn: 'auto',
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(NumberInput, { id: ctrl.key, value: ctrl.value || '', onValue: ctrl.setValue, allowNegative: true, css: {
                            maxWidth: '20rem',
                        } }))));
        }
        if (ctrl.type === 'object') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        gridColumn: '1 / -1',
                        marginBottom: '$4',
                        '@sm': {
                            gridColumn: 'auto',
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(ObjectValueEditor, { id: ctrl.key, value: ctrl.value || '', onValue: ctrl.setValue }))));
        }
        if (ctrl.type === 'file') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        gridColumn: '1 / -1',
                        marginBottom: '$4',
                        '@sm': {
                            gridColumn: 'auto',
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(FileInput, { id: ctrl.key, file: ctrl.value, onFilesSelected: (files) => ctrl.setValue(files[0]) }))));
        }
        if (ctrl.type === 'date') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, { css: {
                        gridColumn: '1 / -1',
                        marginBottom: '$4',
                        '@sm': {
                            gridColumn: 'auto',
                            marginBottom: 0,
                        },
                    } },
                    React.createElement(TextInput, { type: "date", id: ctrl.key, value: ctrl.value ? dateToString(ctrl.value) : '', onChange: (ev) => ctrl.setValue(ev.target.valueAsDate) }))));
        }
        if (ctrl.type === 'color') {
            return (React.createElement(React.Fragment, { key: ctrl.key },
                React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
                React.createElement(ControlWrapper, null,
                    React.createElement(ColorControl, { value: ctrl.value, onChange: ctrl.setValue, id: ctrl.key }))));
        }
        return null;
    })));
};
export const ColorControl = (props) => {
    const [showPopover, setShowPopover] = React.useState(false);
    return (React.createElement(Popover, { onOpenChange: setShowPopover, open: showPopover },
        React.createElement(Popover.Trigger, { asChild: true },
            React.createElement(ColorInput, { id: props.id, value: props.value || '', onValue: props.onChange, css: {
                    maxWidth: '10rem',
                }, placeholder: "#rrggbb" })),
        React.createElement(Popover.Content, null,
            React.createElement(HexColorPicker, { color: props.value, onChange: props.onChange }))));
};
const dateToString = (date) => `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
const PropsSelectControl = (props) => {
    const ctrl = props.control;
    const [type, setType] = React.useState(() => {
        var _a;
        return ctrl.options.some((opt) => opt.type === 'literal')
            ? 'literal'
            : (_a = ctrl.options[0]) === null || _a === void 0 ? void 0 : _a.type;
    });
    const selectedOptionIndex = type === 'literal'
        ? ctrl.options.findIndex((opt) => opt.value === ctrl.value)
        : ctrl.options.findIndex((opt) => opt.type === type);
    const handleSelectOption = (option, index) => {
        if (option.type === 'literal') {
            setType('literal');
            ctrl.setValue(option.value, index);
        }
        else {
            setType(option.type);
            ctrl.setValue(undefined);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Label, { htmlFor: ctrl.key }, ctrl.label),
        React.createElement(ControlWrapper, { css: {
                gridColumn: '1 / -1',
                marginBottom: '$4',
                '@sm': {
                    gridColumn: 'auto',
                    marginBottom: 0,
                },
            } },
            React.createElement(Select, { id: ctrl.key, value: selectedOptionIndex, onValue: (value) => {
                    const valueIndex = Number(value);
                    handleSelectOption(ctrl.options[valueIndex], valueIndex);
                }, css: {
                    maxWidth: '36rem',
                    '@lg': {
                        display: 'none',
                    },
                } }, ctrl.options.map((opt, i) => (React.createElement("option", { value: i, key: i }, opt.label)))),
            React.createElement(ToggleGroup, null, ctrl.options.map((opt, i) => {
                const selected = opt.type === type &&
                    (opt.type !== 'literal' || ctrl.value === opt.value);
                return (React.createElement(SelectButton, { onClick: () => handleSelectOption(opt, i), selected: selected, key: i }, opt.label));
            })),
            type !== 'literal' && (React.createElement(SelectSubControlDiv, null, (function getControl() {
                switch (type) {
                    case 'checkbox':
                        return (React.createElement(Checkbox, { checked: isDefined(ctrl.value) ? ctrl.value : 'indeterminate', onCheckedChange: ctrl.setValue }));
                    case 'file':
                        return (React.createElement(FileInput, { onFilesSelected: (files) => ctrl.setValue(files[0]), file: ctrl.value }));
                    case 'text':
                        return (React.createElement(TextInput, { value: ctrl.value || '', onValue: ctrl.setValue, css: {
                                maxWidth: '36rem',
                            } }));
                    case 'object':
                        return (React.createElement(ObjectValueEditor, { value: ctrl.value || '', onValue: ctrl.setValue }));
                    case 'number':
                        return (React.createElement(NumberInput, { value: ctrl.value || '', onValue: ctrl.setValue, css: {
                                maxWidth: '20rem',
                            }, allowNegative: true }));
                    default:
                        return null;
                }
            })())))));
};
const Root = styled('div', {
    display: 'grid',
    alignItems: 'center',
    py: '$1',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: '$2',
    '@sm': {
        gap: '$2',
    },
});
const Label = styled('label', {
    color: '$gray-600',
    fontFamily: '$mono',
    '@sm': {
        px: '$2',
        py: '$1',
    },
});
const ControlWrapper = styled('div', {
    '@sm': {
        px: '$2',
        py: '$1',
    },
});
export const ToggleGroup = styled('div', {
    display: 'none',
    '@lg': {
        display: 'flex',
        gap: '$2',
        flexWrap: 'wrap',
    },
});
const SelectSubControlDiv = styled('div', {
    marginTop: '$1',
});
const emptyValue = `{

}`;
export const ObjectValueEditor = (props) => {
    const [value, setValue] = React.useState(() => props.value ? stringifyObject(props.value) : emptyValue);
    React.useEffect(() => {
        setValue(props.value ? stringifyObject(props.value) : emptyValue);
    }, [props.value]);
    const isValid = React.useMemo(() => !value || isValidJson(value), [value]);
    return (React.createElement(Textarea, { value: value, onChange: (ev) => setValue(ev.target.value), onBlur: () => {
            if (!value) {
                props.onValue(undefined);
                return;
            }
            if (!props.value && value === emptyValue) {
                return;
            }
            if (isValid) {
                props.onValue(parseJsObject(value));
            }
        }, id: props.id, css: isValid
            ? undefined
            : {
                borderColor: '$red-400',
            } }));
};
// we don't use JSON.parse because valid js object (prop without quote) is invalid JSON
const parseJsObject = (jsObjString) => safeEval(`return (${jsObjString});`, {});
const isValidJson = (val) => {
    try {
        parseJsObject(val);
        return true;
    }
    catch (err) {
        return false;
    }
};
export const SelectButton = styled(Button, {
    px: '$3',
    py: '$1',
    color: '$gray-600',
    backgroundColor: '$gray-100',
    cursor: 'pointer',
    variants: {
        selected: {
            true: {
                backgroundColor: '$primary-700',
                color: 'White',
            },
        },
    },
});
//# sourceMappingURL=props-editor.js.map